/* eslint-disable */
import LoanCard from "components/loan-card/LoanCard";
import { MyLoanInfo, useGetMyLoansQuery, useSendUserInteractedMutation } from "pages/application/applicationService";
import { ConsentTypes, useSetConsentMutation } from "pages/authentication/userService";
import "./loans-list.scss";
import Footer from "components/footer/Footer";
import { getCurrentUserId, getDealApplicationId, setApplicationRoutingData, setPreferredLanguage } from "features/authSlice";
import { setHasLoans, isPostLosLoan, setIsLanguageToggleLocked } from "features/preferredLanguageSlice";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate } from "utilities/date";
import { useEffect } from "react";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import noLoanImage from "assets/images/v2/no-loan.svg";
import RedesignAnnouncementModal from "pages/announcements/re-design/re-design-modal";
import { useGetBorrowerDetailsQuery } from "pages/dashboard/home/dashboardService";
import { normalizeToAppLanguage } from "utilities/getPreferredLanguage";

export default function LoansList() {
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const { data, isLoading, isFetching } = useGetMyLoansQuery(userId);
  const [sendRequest] = useSetConsentMutation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pathsToShowMyLoans = ["/application", "/dashboard"];
  const [sendUserInteracted] = useSendUserInteractedMutation();

   const { data: userProfile } = useGetBorrowerDetailsQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });
  
  useEffect(() => {
    if (dealApplicationId) {
      return;
    }

    if (!userProfile?.preferredLanguage) {
      return;
    }

    const resolved = normalizeToAppLanguage(userProfile.preferredLanguage);
    dispatch(setPreferredLanguage(resolved));
  }, [userProfile?.preferredLanguage, dealApplicationId, dispatch]);

  useEffect(() => {
    if (isLoading) { return }
    if (data) {
      dispatch(setIsLanguageToggleLocked(data.length > 0 && data.every((loan) => isPostLosLoan(loan.loanNumber))));
      if (data.length === 1) {
        if (
          !(
            (
              location.state != null &&
              typeof location.state === "object" &&
              "previousPath" in location.state &&
              pathsToShowMyLoans.some((str) => location.state.previousPath.includes(str))
            ) // eslint-disable-line
          )
        ) {
          navigateToPostAuth(0, data);
        }
      } else if (data.length > 1) {
        data.forEach((myLoan) => {
          sendRequest({
            id: myLoan.nhDealApplicationId,
            consent: {
              userId: userId,
              nhDealApplicationId: myLoan.nhDealApplicationId,
              prospectId: myLoan.prospectId,
              consentTypes: [ConsentTypes.PrivacyPolicy, ConsentTypes.TermsOfUse],
            },
          });
        });
      }
    }
  }, [data, isLoading]);

  const navigateToPostAuth = (index: number, loans: MyLoanInfo[]) => {
    const {
      nhApplicationId,
      nhApplicationStatus,
      nhDealApplicationId,
      applicationRoleType,
      dealApplicationId,
      loanId,
      hasPal,
      prospectId,
      loanPurpose,
      leadProvider,
      isConsentCollected,
      userRole,
      inquiryId,
      coBorrowerHasOwnNhApplication,
      nhApplicationPath,
      loanNumber,
      applicationNumber,
      userType,
      encompassLoanId,
      dealId,
      dealBorrowerId
    } = loans[index];
    sendUserInteracted({
      applicationId: dealApplicationId || "",
      prospectId: prospectId || "",
      userType: userType,
    });
    dispatch(
      setApplicationRoutingData({
        nhApplicationId,
        nhApplicationStatus,
        nhDealApplicationId,
        applicationRoleType,
        dealApplicationId,
        loanId,
        hasPal,
        prospectId,
        loanPurpose,
        leadProvider,
        isConsentCollected,
        userRole,
        inquiryId,
        coBorrowerHasOwnNhApplication,
        nhApplicationPath,
        loanNumber,
        applicationNumber,
        encompassLoanId,
        dealId,
        dealBorrowerId
      }),
    );
    navigate("/post-auth", { replace: true });
  };

  const handleLoanCardClick = (index: number) => {
    data && navigateToPostAuth(index, data);
  };
  const hasLoans = data && data.length > 0;

  if (isFetching || isLoading) {
    return <Overlay />;
  } else if (!isLoading && data && data.length === 1) {
    return <RedesignAnnouncementModal />;
  } else
    return (
      <>
        <RedesignAnnouncementModal />
        <div className="loans-section">
          <div className={`loans-section__wrapper ${!hasLoans ? "no-loans-full-width" : ""}`}>
            <div className="loans-section__header">
              <span className="loans-section__header-title">My Loans</span>
            </div>

            {!hasLoans ? (
              <>
                <div className="loans-section__header-subtitle">Select a loan below to view your application details.</div>
                <div className="loans-section__no-loans">
                  <div className="loans-section__no-loans__content">
                    <div className="loans-section__no-loans__icon">
                      <img src={noLoanImage} alt="no-loan" />
                    </div>
                    <div className="loans-section__no-loans__content-text">
                      <div className="loans-section__no-loans__content-text__title">No Loans Available.</div>
                      <div className="loans-section__no-loans__content-text__description">
                        It looks like you don't have any active loans. If you have questions, feel free to connect with your Loan Officer or <br />
                        give us a call at{" "}
                        <a className="loans-section__no-loans__phone-link" href="tel:8772144431">
                          (877) 214-4431
                        </a>
                        .
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="loans-section__content">
                <div className="loans-section__header-subtitle">Select a loan below to view your application details.</div>
                <div className="loans-section__card-container">
                  {data?.map((item, index) => {
                    const {
                      property,
                      loanPurpose,
                      loanAmount,
                      status,
                      startedDate,
                      documentStatus,
                      loanNumber,
                      nhDealApplicationId,
                      applicationNumber,
                    } = item;
                    return (
                      <div className="loans-section__card" key={index}>
                        <LoanCard
                          property={property}
                          loanPurpose={loanPurpose}
                          loanAmount={loanAmount}
                          status={status}
                          startedDate={formatDate(startedDate)}
                          documentStatus={documentStatus}
                          handleLoanCardClick={handleLoanCardClick}
                          index={index}
                          loanNumber={loanNumber}
                          nhDealApplicationId={nhDealApplicationId}
                          applicationNumber={applicationNumber}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="my-loans-footer">
          <Footer />
        </div>
      </>
    );
}
